import { useCallback, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Card, StatCard } from '../../components/Card';
import { StatusBadge } from '../../components/StatusBadge';
import type {
  DirectoryWorkspaceDetailResponse,
} from '../../types/api';
import { useAdminResource } from './useAdminResource';
import { DirectoryError, FactList, backTarget } from './parts';
import {
  PLAN_BASIS_TEXT,
  formatDate,
  formatDateTime,
  formatNumber,
  humanize,
  yesNo,
} from './labels';
import { Breadcrumb, CardsSkeleton, secondaryButton } from '../../components/admin/AdminUI';
import type { AdminAppAccessResponse } from '../../types/api';
import { AddMemberDialog, AppAccessCard, ManagedMembersList, WorkspaceActions } from './WorkspaceManagement';

type Detail = DirectoryWorkspaceDetailResponse;

function PlanCard({ subscription }: { subscription: Detail['subscription'] }) {
  if (!subscription) {
    return (
      <Card title="Plan">
        <p className="text-sm text-slate-500">
          No plan record yet. Central creates one the first time someone opens this workspace.
        </p>
      </Card>
    );
  }
  const { plan } = subscription;
  const differs = plan.onRecordCode !== null && plan.onRecordCode !== plan.code;
  return (
    <Card title="Plan">
      <FactList
        items={[
          { label: 'Plan in effect', value: plan.name ?? '—' },
          ...(differs ? [{ label: 'Plan on record', value: plan.onRecordName ?? plan.onRecordCode ?? '—' }] : []),
          { label: 'Why', value: PLAN_BASIS_TEXT[plan.basis] ?? humanize(plan.basis) },
          { label: 'Subscription status', value: humanize(subscription.status) },
          { label: 'Trial ends', value: formatDateTime(subscription.trialEndsAt) },
          {
            label: 'Current period',
            value:
              subscription.currentPeriodStart || subscription.currentPeriodEnd
                ? `${formatDate(subscription.currentPeriodStart)} to ${formatDate(subscription.currentPeriodEnd)}`
                : '—',
          },
          { label: 'Cancels at period end', value: yesNo(subscription.cancelAtPeriodEnd) },
          { label: 'Canceled', value: formatDateTime(subscription.canceledAt) },
          { label: 'Billing provider', value: subscription.provider ? humanize(subscription.provider) : 'None' },
        ]}
      />
    </Card>
  );
}

function CreditsCard({ credits, reason }: { credits: Detail['credits']; reason: Detail['creditsUnavailableReason'] }) {
  if (!credits) {
    return (
      <Card title="Credits">
        <p className="text-sm text-slate-500">
          {reason === 'paused'
            ? 'Credit reads are switched off in Central right now, so the balance is not shown.'
            : 'Could not load the credit balance.'}
        </p>
      </Card>
    );
  }
  return (
    <Card title="Credits">
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard label="Available" value={formatNumber(credits.available)} />
          <StatCard label="Balance" value={formatNumber(credits.balance)} />
          <StatCard label="On hold" value={formatNumber(credits.onHold)} />
        </div>
        <FactList
          items={[
            { label: 'From trial', value: formatNumber(credits.bySource.trial) },
            { label: 'Included with the plan', value: formatNumber(credits.bySource.subscription_included) },
            { label: 'Purchased', value: formatNumber(credits.bySource.purchased) },
            { label: 'Promotional', value: formatNumber(credits.bySource.promotional) },
            { label: 'Ledger entries', value: formatNumber(credits.ledgerEntries) },
            { label: 'Last ledger entry', value: formatDateTime(credits.lastEntryAt) },
          ]}
        />
        <p className="text-xs text-slate-500">
          As recorded in the credit ledger. Credits that expired since the balance was last checked still count
          here until Central records the expiry.
        </p>
      </div>
    </Card>
  );
}

function OnboardingCard({ onboarding }: { onboarding: Detail['onboarding'] }) {
  return (
    <Card title="Onboarding">
      <FactList
        items={[
          {
            label: 'Finished',
            value: onboarding.finished ? (
              <StatusBadge status="success" label="Finished" />
            ) : (
              <StatusBadge status="warning" label="Not finished" />
            ),
          },
          { label: 'Finished on', value: formatDateTime(onboarding.completedAt) },
          { label: 'Current step', value: humanize(onboarding.step) },
          { label: 'Setup type', value: humanize(onboarding.mode) },
          {
            label: 'Outcomes picked during setup',
            value: onboarding.selectedOutcomes.length ? onboarding.selectedOutcomes.join(', ') : 'None',
          },
          { label: 'Pricing step done', value: yesNo(onboarding.pricingComplete) },
          { label: 'Review step done', value: yesNo(onboarding.reviewComplete) },
          { label: 'Invites sent', value: yesNo(onboarding.invitesSent) },
          { label: 'Started', value: formatDateTime(onboarding.startedAt) },
          { label: 'Onboarding version', value: onboarding.version ?? '—' },
        ]}
      />
    </Card>
  );
}

/**
 * Admin console: one workspace — members, apps switched on, outcomes, plan,
 * credits and onboarding. Read-only.
 */
export function WorkspaceDetailPage() {
  const { workspaceId = '' } = useParams();
  const location = useLocation();
  const { data, error, loading, reload } = useAdminResource<Detail>(
    `/workspaces/${encodeURIComponent(workspaceId)}`,
  );
  const back = backTarget(location.state, '/workspaces');
  const [version, setVersion] = useState(0);
  const [access, setAccess] = useState<AdminAppAccessResponse | null>(null);
  const [adding, setAdding] = useState(false);
  const changed = useCallback(() => {
    reload();
    setVersion((n) => n + 1);
  }, [reload]);
  const onAccessLoaded = useCallback((a: AdminAppAccessResponse | null) => setAccess(a), []);
  const appRolesByUser = useMemo(() => new Map((access?.members ?? []).map((m) => [m.userId, m])), [access]);

  return (
    <div className="space-y-6">
      <Link to={back} className="inline-flex text-sm font-medium text-slate-500 hover:text-emerald-700">
        ← All workspaces
      </Link>

      {loading ? (
        <CardsSkeleton message="Loading this workspace…" />
      ) : error ? (
        <DirectoryError error={error} what="This workspace" onRetry={reload} />
      ) : data ? (
        <>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Breadcrumb items={[{ label: 'Workspaces', to: back }, { label: 'Workspace' }]} />
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900 break-words">{data.workspace.name}</h2>
              <p className="text-sm text-slate-500">
                {humanize(data.workspace.type)} workspace · {humanize(data.workspace.status)}
              </p>
            </div>
            <WorkspaceActions
              workspace={data.workspace}
              onChanged={changed}
              currentPlanCode={data.subscription?.plan.onRecordCode ?? data.subscription?.plan.code ?? null}
            />
          </div>
          {data.workspace.status === 'closed' && (
            <p role="status" className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-inset ring-amber-600/20">
              This workspace is closed: nobody can sign in to it or open its apps. Reopen it from the actions menu.
            </p>
          )}

          <Card title="Details">
            <FactList
              items={[
                { label: 'Workspace ID', value: <span className="font-mono text-xs">{data.workspace.id}</span> },
                { label: 'Type', value: humanize(data.workspace.type) },
                { label: 'Status', value: humanize(data.workspace.status) },
                { label: 'Made for', value: humanize(data.workspace.entryType) },
                { label: 'Created', value: formatDateTime(data.workspace.createdAt) },
                { label: 'Closed', value: formatDateTime(data.workspace.closedAt) },
              ]}
            />
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PlanCard subscription={data.subscription} />
            <CreditsCard credits={data.credits} reason={data.creditsUnavailableReason} />
          </div>

          <OnboardingCard onboarding={data.onboarding} />

          <AppAccessCard key={version} workspace={data.workspace} onChanged={changed} onLoaded={onAccessLoaded} />

          <Card
            title={`Members (${data.memberTotal})`}
            actions={
              <button
                type="button"
                className={secondaryButton}
                onClick={() => setAdding(true)}
                disabled={data.workspace.status === 'closed'}
              >
                Add member
              </button>
            }
          >
            <ManagedMembersList
              workspace={data.workspace}
              members={data.members}
              apps={access?.apps ?? null}
              appRolesByUser={appRolesByUser}
              onChanged={changed}
            />
            <AddMemberDialog
              open={adding}
              workspace={data.workspace}
              onClose={() => setAdding(false)}
              onDone={() => {
                setAdding(false);
                changed();
              }}
            />
            {data.memberTotal > data.members.length && (
              <p className="mt-3 text-xs text-slate-500">
                Showing the first {data.members.length} of {data.memberTotal} members.
              </p>
            )}
          </Card>
        </>
      ) : null}
    </div>
  );
}
